import { useState } from 'react';
import { motion } from 'framer-motion';
import { useGameStore, useIsHost, usePlayers } from '../stores/gameStore';
import { useGameActions, useSocket } from '../hooks/useSocket';
import AdminPanelView from './AdminPanelView';

const LobbyView = () => {
  const [confirmKickId, setConfirmKickId] = useState<string | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);

  const { gameState, clientState } = useGameStore();
  const isHost = useIsHost();
  const players = usePlayers();
  const { startGame, kickPlayer, setConfig } = useGameActions();
  const { leaveRoom } = useSocket();

  const handleLeaveClick = () => {
    if (confirmLeave) {
      leaveRoom();
    } else {
      setConfirmLeave(true);
      setTimeout(() => setConfirmLeave(false), 3000);
    }
  };

  const playerCount = players.length;
  const gridCols = playerCount > 8 ? 'grid-cols-3 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4';

  const handleKickClick = (id: string) => {
    if (confirmKickId === id) {
      kickPlayer(id);
      setConfirmKickId(null);
    } else {
      setConfirmKickId(id);
      setTimeout(() => setConfirmKickId(null), 3000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="w-full max-w-4xl flex flex-col gap-6 px-4 pb-24"
    >
      {/* Leave Button */}
      <div className="flex justify-end mb-2">
        <button
          onClick={handleLeaveClick}
          className={`px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-wider transition-all ${
            confirmLeave
              ? 'bg-red-600 text-white animate-pulse'
              : 'bg-white/10 text-white/50 hover:bg-white/20 hover:text-white'
          }`}
        >
          {confirmLeave ? 'Tap to confirm leave' : 'Leave Room'}
        </button>
      </div>

      {/* Room Code Display */}
      <div className="text-center space-y-2">
        <h2 className="text-gray-500 font-black uppercase tracking-widest text-[10px]">
          Room Code
        </h2>
        <div className="text-5xl font-black text-neon-blue tracking-wider bg-white/5 inline-block px-8 py-3 rounded-2xl border-2 border-white/10 shadow-[0_0_30px_rgba(6,182,212,0.1)]">
          {gameState.roomCode}
        </div>
        <p className="text-gray-500 font-bold text-sm">Waiting for players...</p>
      </div>

      {/* Player Grid */}
      <div className={`grid ${gridCols} gap-3`}>
        {players.map((p, i) => {
          const isMe = p.id === clientState.myId;
          const isHostPlayer = p.id === gameState.hostId;
          const isConfirming = confirmKickId === p.id;

          return (
            <motion.div
              key={p.id}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: i * 0.05 }}
              className={`glass p-4 rounded-2xl flex flex-col items-center gap-2 relative group transition-all border-2 ${
                isMe
                  ? 'border-neon-blue bg-neon-blue/5 shadow-[0_0_15px_rgba(6,182,212,0.2)]'
                  : 'border-white/10'
              } ${isConfirming ? 'border-red-600 bg-red-600/10' : ''}`}
            >
              {/* Kick button (host only, not self) */}
              {isHost && !isMe && (
                <button
                  type="button"
                  onClick={() => handleKickClick(p.id)}
                  className={`absolute -top-1 -right-1 h-6 px-2 rounded-full flex items-center justify-center font-black text-[8px] shadow-lg z-10 transition-all ${
                    isConfirming
                      ? 'bg-red-600 text-white w-auto scale-110'
                      : 'bg-red-600 text-white w-6 hover:scale-110'
                  }`}
                >
                  {isConfirming ? 'KICK?' : '✕'}
                </button>
              )}

              {/* Avatar */}
              <div className="relative">
                <span className={playerCount > 8 ? 'text-3xl' : 'text-4xl'}>{p.avatar}</span>
                {isMe && (
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    className="absolute -top-2 -right-2 bg-neon-blue text-white px-1 py-0.5 rounded text-[7px] font-black uppercase shadow-[0_0_10px_rgba(6,182,212,0.5)]"
                  >
                    You
                  </motion.div>
                )}
              </div>

              {/* Name */}
              <span
                className={`font-black text-[10px] sm:text-xs truncate w-full text-center ${
                  isMe ? 'text-neon-blue' : 'text-white'
                }`}
              >
                {p.name}
              </span>

              {/* Host badge */}
              {isHostPlayer && (
                <span className="text-[7px] bg-neon-purple px-1.5 py-0.5 rounded font-black uppercase tracking-tighter">
                  Host
                </span>
              )}

              {/* Online status */}
              {!p.isOnline && (
                <span className="text-[7px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-black uppercase">
                  Offline
                </span>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Admin Panel (Host only) */}
      <div className="flex flex-col gap-6">
        {isHost && (
          <div className="glass p-5 rounded-2xl border-t-4 border-neon-purple shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
            <AdminPanelView config={gameState.config} playerCount={playerCount} onUpdate={setConfig} />
          </div>
        )}

        {/* Start Button */}
        <div className="fixed bottom-0 left-0 right-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-black/80 backdrop-blur-lg flex justify-center z-50 border-t border-white/5">
          {isHost ? (
            <button
              type="button"
              onClick={startGame}
              disabled={players.length < 2 && !gameState.config.devMode}
              className={`w-full max-w-md py-4 rounded-xl font-black text-xl transition-all ${
                players.length < 2 && !gameState.config.devMode
                  ? 'bg-gray-700 opacity-50 cursor-not-allowed text-white/50'
                  : 'bg-neon-purple hover:bg-neon-purple/80 active:scale-95 shadow-[0_0_30px_rgba(217,70,239,0.3)]'
              }`}
            >
              START GAME ({players.length}/14)
            </button>
          ) : (
            <div className="text-neon-purple font-black text-lg animate-pulse uppercase italic py-2">
              Waiting for host...
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default LobbyView;
